import authService from './auth.service';
import type { Agreement, AgreementDetail, AgreementListParams, AgreementGroup, StageHistoryListResponse, StageHistoryDetail, StageHistoryServiceType } from '../types/agreement.types';

const API_BASE_URL = '/api/v1';

class AgreementsService {
  /**
   * Получить список договоров с пагинацией и фильтрами
   * Использует POST /api/v1/agreements/search
   * Требует пермишн: agreements:list
   *
   * Фильтры передаются как есть — трансформация уже сделана в buildApiFilters.
   * Формат range фильтров: { key: { from, to } }
   * Формат array фильтров: { key: [...] }
   */
  async getAgreements(params?: AgreementListParams): Promise<{ items: Agreement[], total: number, page_size: number, groups?: AgreementGroup[] | null }> {
    const { limit = 20, offset = 0, group_by, order_by, ...filters } = params || {};

    // Фильтруем пустые значения
    const filtersBody: Record<string, unknown> = {};

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        // Пустые массивы пропускаем
        if (Array.isArray(value) && value.length === 0) return;
        // Пустые range объекты пропускаем
        if (typeof value === 'object' && !Array.isArray(value)) {
          const rangeVal = value as { from?: unknown; to?: unknown };
          if (rangeVal.from === undefined && rangeVal.to === undefined) return;
        }

        filtersBody[key] = value;
      }
    });

    const requestBody: Record<string, unknown> = {
      filters: filtersBody,
      limit,
      offset,
    };

    if (group_by && group_by.length > 0) {
      requestBody.group_by = group_by;
    }

    if (order_by && order_by.length > 0) {
      requestBody.order_by = order_by;
    }

    const res = await authService.fetchWithAuth(
      `${API_BASE_URL}/agreements/search`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      }
    );

    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить договора');
    }

    const data = await res.json();

    // Поддержка разных форматов ответа от backend
    if (Array.isArray(data)) {
      return { items: data, total: data.length, page_size: limit, groups: null };
    }
    if (data && Array.isArray(data.items)) {
      return {
        items: data.items,
        total: data.total || data.items.length,
        page_size: data.page_size || limit,
        groups: data.groups || null,
      };
    }

    return { items: [], total: 0, page_size: limit, groups: null };
  }

  /**
   * Получить договор по ID
   * Требует пермишн: agreements:read
   */
  async getAgreementById(id: string): Promise<AgreementDetail> {
    const res = await authService.fetchWithAuth(
      `${API_BASE_URL}/agreements/${id}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }
    );

    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить договор');
    }

    return res.json();
  }

  /**
   * Создать новый договор
   * Требует пермишн: agreements:create
   */
  async createAgreement(data: Partial<Agreement>): Promise<Agreement> {
    const res = await authService.fetchWithAuth(
      `${API_BASE_URL}/agreements`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }
    );

    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось создать договор');
    }

    return res.json();
  }

  /**
   * Обновить договор
   * Требует пермишн: agreements:update
   */
  async updateAgreement(id: string, data: Partial<Agreement>): Promise<Agreement> {
    const res = await authService.fetchWithAuth(
      `${API_BASE_URL}/agreements/${id}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      }
    );

    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось обновить договор');
    }

    return res.json();
  }

  /**
   * Получить историю изменения этапов
   * GET /api/v1/stage-history/{serviceType}/{entityId}
   */
  async getStageHistory(serviceType: StageHistoryServiceType, entityId: number, params?: { limit?: number; offset?: number }): Promise<StageHistoryListResponse> {
    const limit = params?.limit ?? 20;
    const offset = params?.offset ?? 0;

    const res = await authService.fetchWithAuth(
      `${API_BASE_URL}/stage-history/${serviceType}/${entityId}?limit=${limit}&offset=${offset}`
    );

    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить историю этапов');
    }

    return res.json();
  }

  /**
   * Получить детализацию записи истории этапов
   * GET /api/v1/stage-history/{serviceType}/{entityId}/{historyId}
   */
  async getStageHistoryDetail(serviceType: StageHistoryServiceType, entityId: number, historyId: number): Promise<StageHistoryDetail> {
    const res = await authService.fetchWithAuth(
      `${API_BASE_URL}/stage-history/${serviceType}/${entityId}/${historyId}`
    );

    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить детали записи');
    }

    return res.json();
  }

  /**
   * Удалить договор
   * Требует пермишн: agreements:delete
   */
  async deleteAgreement(id: string): Promise<void> {
    const res = await authService.fetchWithAuth(
      `${API_BASE_URL}/agreements/${id}`,
      {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      }
    );

    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось удалить договор');
    }
  }
}

export const agreementsService = new AgreementsService();
